// ─── PSC National Team Management System — Menu ──────────────────────────────
// Philippine Sports Commission (PSC)
// Module structure:
//   1. Athlete Profile Management
//   2. Payroll
//   3. Medical History
//   4. Nutrition
//   5. Dormitories
// ─────────────────────────────────────────────────────────────────────────────

export function buildNationalTeamMenuItems(state) {

    // ── State variables (collapsed/expanded per group) ────────────────────────
    var isNtAthleteProfile  = state.isNtAthleteProfile;
    var isNtPayroll         = state.isNtPayroll;
    var isNtMedical         = state.isNtMedical;
    var isNtNutrition       = state.isNtNutrition;
    var isNtDormitory       = state.isNtDormitory;

    // ── Setters ───────────────────────────────────────────────────────────────
    var setIsNtAthleteProfile  = state.setIsNtAthleteProfile;
    var setIsNtPayroll         = state.setIsNtPayroll;
    var setIsNtMedical         = state.setIsNtMedical;
    var setIsNtNutrition       = state.setIsNtNutrition;
    var setIsNtDormitory       = state.setIsNtDormitory;
    var setIscurrentState      = state.setIscurrentState;

    return [
        {
            id: "national-team",
            label: "National Team",
            icon: "ri-medal-line",
            link: "/#",
            subItems: [

                // ── Dashboard ────────────────────────────────────────────────
                {
                    id: "nt-dashboard",
                    label: "Dashboard",
                    icon: "ri-dashboard-2-line",
                    link: "/national-team/dashboard",
                    parentId: "national-team",
                    permissionTypeId: [],
                },

                // ── 1. ATHLETE PROFILE MANAGEMENT ────────────────────────────
                // Covers athlete registration, personal info, sport assignment,
                // emergency contacts, documents, and status tracking.
                // ────────────────────────────────────────────────────────────
                {
                    id: "nt-athlete-profile",
                    label: "Athlete Management",
                    icon: "ri-user-star-line",
                    link: "/#",
                    parentId: "national-team",
                    isChildItem: true,
                    stateVariables: isNtAthleteProfile,
                    click: function (e) {
                        e.preventDefault();
                        setIsNtAthleteProfile(!isNtAthleteProfile);
                        setIscurrentState("NtAthleteProfile");
                    },
                    childItems: [
                        {
                            // Master list of all registered national athletes
                            id: "nt-athletes",
                            label: "Athletes",
                            link: "/national-team/athletes",
                            permissionTypeId: [],
                        },
                        {
                            // Sports disciplines / events managed by PSC
                            id: "nt-sports",
                            label: "Sports & Disciplines",
                            link: "/national-team/athletes/sports",
                            permissionTypeId: [],
                        },
                        {
                            // Coaching staff assignments per sport / athlete
                            id: "nt-coaching-staff",
                            label: "Coaching Staff",
                            link: "/national-team/athletes/coaching-staff",
                            permissionTypeId: [],
                        },
                        {
                            // Competition history, results, rankings
                            id: "nt-competition-history",
                            label: "Competition History",
                            link: "/national-team/athletes/competition-history",
                            permissionTypeId: [],
                        },
                        {
                            // Grants, incentives, and recognition records
                            id: "nt-grants-incentives",
                            label: "Grants & Incentives",
                            link: "/national-team/athletes/grants-incentives",
                            permissionTypeId: [],
                        },
                        {
                            // Documents: contracts, waivers, IDs, eligibility papers
                            id: "nt-athlete-documents",
                            label: "Athlete Documents",
                            link: "/national-team/athletes/documents",
                            permissionTypeId: [],
                        },
                    ],
                },

                // ── 3. MEDICAL HISTORY ────────────────────────────────────────
                // Physical exam records, injury logs, clearances,
                // rehabilitation, and anti-doping records.
                // ────────────────────────────────────────────────────────────
                {
                    id: "nt-medical",
                    label: "Medical History",
                    icon: "ri-heart-pulse-line",
                    link: "/#",
                    parentId: "national-team",
                    isChildItem: true,
                    stateVariables: isNtMedical,
                    click: function (e) {
                        e.preventDefault();
                        setIsNtMedical(!isNtMedical);
                        setIscurrentState("NtMedical");
                    },
                    childItems: [
                        {
                            // Pre- and post-competition physical examinations
                            id: "nt-medical-exams",
                            label: "Physical Examinations",
                            link: "/national-team/medical/exams",
                            permissionTypeId: [],
                        },
                        {
                            // Injury incidents: type, severity, date, sport event
                            id: "nt-injury-log",
                            label: "Injury Log",
                            link: "/national-team/medical/injuries",
                            permissionTypeId: [],
                        },
                        {
                            // Rehabilitation plans and recovery progress tracking
                            id: "nt-rehabilitation",
                            label: "Rehabilitation & Recovery",
                            link: "/national-team/medical/rehabilitation",
                            permissionTypeId: [],
                        },
                        {
                            // Medical clearances for training and competition
                            id: "nt-medical-clearance",
                            label: "Medical Clearance",
                            link: "/national-team/medical/clearance",
                            permissionTypeId: [],
                        },
                        {
                            // Anti-doping test records and WADA/NADO compliance
                            id: "nt-anti-doping",
                            label: "Anti-Doping Records",
                            link: "/national-team/medical/anti-doping",
                            permissionTypeId: [],
                        },
                        {
                            // Prescribed medicines and supplements log per athlete
                            id: "nt-medications",
                            label: "Medications & Prescriptions",
                            link: "/national-team/medical/medications",
                            permissionTypeId: [],
                        },
                        {
                            // Medical staff: team physicians, therapists, sports doctors
                            id: "nt-medical-staff",
                            label: "Medical Staff",
                            link: "/national-team/medical/staff",
                            permissionTypeId: [],
                        },
                    ],
                },

                // ── 4. NUTRITION ──────────────────────────────────────────────
                // Meal plans, dietary assessments, supplement tracking,
                // nutritionist assignments, and body composition monitoring.
                // ────────────────────────────────────────────────────────────
                {
                    id: "nt-nutrition",
                    label: "Nutrition",
                    icon: "ri-plant-line",
                    link: "/#",
                    parentId: "national-team",
                    isChildItem: true,
                    stateVariables: isNtNutrition,
                    click: function (e) {
                        e.preventDefault();
                        setIsNtNutrition(!isNtNutrition);
                        setIscurrentState("NtNutrition");
                    },
                    childItems: [
                        {
                            // Individualized daily meal plans per athlete and sport
                            id: "nt-meal-plans",
                            label: "Meal Plans",
                            link: "/national-team/nutrition/meal-plans",
                            permissionTypeId: [],
                        },
                        {
                            // Dietary intake assessments and nutritional analysis
                            id: "nt-dietary-assessment",
                            label: "Dietary Assessment",
                            link: "/national-team/nutrition/dietary-assessment",
                            permissionTypeId: [],
                        },
                        {
                            // Body weight, BMI, body fat, muscle mass tracking over time
                            id: "nt-body-composition",
                            label: "Body Composition Monitoring",
                            link: "/national-team/nutrition/body-composition",
                            permissionTypeId: [],
                        },
                        {
                            // Approved supplements, dosage schedules, and WADA compliance
                            id: "nt-supplements",
                            label: "Supplements & Vitamins",
                            link: "/national-team/nutrition/supplements",
                            permissionTypeId: [],
                        },
                        {
                            // Nutritionists assigned per sport or athlete group
                            id: "nt-nutritionists",
                            label: "Nutritionists",
                            link: "/national-team/nutrition/nutritionists",
                            permissionTypeId: [],
                        },
                        {
                            // Hydration monitoring especially during training camps
                            id: "nt-hydration",
                            label: "Hydration Monitoring",
                            link: "/national-team/nutrition/hydration",
                            permissionTypeId: [],
                        },
                    ],
                },

                // ── 5. DORMITORIES ────────────────────────────────────────────
                // Room assignments, occupancy records, maintenance,
                // visitor logs, facility management, and billings.
                // ────────────────────────────────────────────────────────────
                {
                    id: "nt-dormitory",
                    label: "Dormitories",
                    icon: "ri-building-4-line",
                    link: "/#",
                    parentId: "national-team",
                    isChildItem: true,
                    stateVariables: isNtDormitory,
                    click: function (e) {
                        e.preventDefault();
                        setIsNtDormitory(!isNtDormitory);
                        setIscurrentState("NtDormitory");
                    },
                    childItems: [
                        {
                            // Room/bed assignments per athlete; check-in and check-out
                            id: "nt-room-assignments",
                            label: "Room Assignments",
                            link: "/national-team/dormitory/room-assignments",
                            permissionTypeId: [],
                        },
                        {
                            // Real-time occupancy per building, floor, and room
                            id: "nt-occupancy",
                            label: "Occupancy Records",
                            link: "/national-team/dormitory/occupancy",
                            permissionTypeId: [],
                        },
                        {
                            // Buildings, floors, rooms, and bed inventory
                            id: "nt-facilities",
                            label: "Facilities & Rooms",
                            link: "/national-team/dormitory/facilities",
                            permissionTypeId: [],
                        },
                        {
                            // Maintenance requests and repair history per room / facility
                            id: "nt-maintenance",
                            label: "Maintenance Requests",
                            link: "/national-team/dormitory/maintenance",
                            permissionTypeId: [],
                        },
                        {
                            // Visitor logbook with check-in/check-out and authorization
                            id: "nt-visitor-log",
                            label: "Visitor Log",
                            link: "/national-team/dormitory/visitor-log",
                            permissionTypeId: [],
                        },
                        {
                            // House rules violations and incident reports
                            id: "nt-incidents",
                            label: "Incident Reports",
                            link: "/national-team/dormitory/incidents",
                            permissionTypeId: [],
                        },
                        {
                            // Dormitory fees, billing history, and payment tracking
                            id: "nt-billing",
                            label: "Billing & Fees",
                            link: "/national-team/dormitory/billing",
                            permissionTypeId: [],
                        },
                    ],
                },

                // ── Reports & Settings ────────────────────────────────────────
                {
                    id: "nt-reports",
                    label: "Reports",
                    icon: "ri-bar-chart-box-line",
                    link: "/national-team/reports",
                    parentId: "national-team",
                    permissionTypeId: [],
                },
                {
                    id: "nt-settings",
                    label: "Settings",
                    icon: "ri-settings-4-line",
                    link: "/national-team/settings",
                    parentId: "national-team",
                    permissionTypeId: [],
                },

            ],
        },
    ];
}